/**
 * Appeal Builder -- assemble a payer-ready appeal packet from any claim or
 * denial case on demand.
 *
 * Route: /AppealBuilder
 *
 * Now hosted inside the shared workflow shell (Choose -> Upload -> Run ->
 * Results) so the page matches the rest of the suite. The "Upload" step is
 * marked skippable -- the source is picked from existing denial cases /
 * claims, not uploaded raw -- but optional supporting documents can still
 * be dropped in.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  getWorkflowModule,
} from '../../components/workflow';
import {
  buildAppealPacket,
  fetchAppealCalendar,
  fetchAppealPrediction,
  listClaims,
  listDenials,
  markDeadlineFiled,
  recomputeDeadlines,
} from '../../services/suiteApi';
import { getSampleById } from '../../fixtures/testCases';
import './suite.css';

function formatCurrency(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function summariseSource(source, packet, prediction) {
  if (!source && !packet) return '';
  const lines = [
    `APPEAL BUILDER — ${source ? `${source.kind === 'claim' ? 'Claim' : 'Denial case'} ${source.reference}` : 'sample case'}`,
  ];
  if (source?.patient) lines.push(`Patient: ${source.patient}`);
  if (source?.payer) lines.push(`Payer: ${source.payer}`);
  if (source?.amount != null) lines.push(`Amount: ${formatCurrency(source.amount)}`);
  if (packet?.recommendedAction) lines.push(`Recommended action: ${packet.recommendedAction}`);
  if (packet?.denial?.carcCodes?.length) lines.push(`CARC: ${packet.denial.carcCodes.join(', ')}`);
  if (prediction?.winProbability != null) lines.push(`Predicted win probability: ${Math.round(prediction.winProbability * 100)}%`);
  if (packet?.appealLetter) {
    lines.push('', '--- Appeal letter ---', String(packet.appealLetter));
  }
  return lines.join('\n');
}

function SourcePicker({ sources, onSelect, selectedKey, busy }) {
  if (!sources?.length) {
    return (
      <div className="dm-card__empty">
        No claims or denial cases on the account yet. Add some through{' '}
        <Link to="/intake/new">Upload / Intake</Link>, or run a sample below.
      </div>
    );
  }
  return (
    <table className="suite-table">
      <thead>
        <tr>
          <th>Type</th><th>Reference</th><th>Patient</th><th>Payer</th><th>Status</th><th>Amount</th><th></th>
        </tr>
      </thead>
      <tbody>
        {sources.map((s) => (
          <tr key={s.key} style={{ background: selectedKey === s.key ? '#eff6ff' : undefined }}>
            <td>
              <span className={`suite-badge ${s.kind === 'claim' ? 'suite-badge--info' : 'suite-badge--warn'}`}>
                {s.kind === 'claim' ? 'Claim' : 'Denial'}
              </span>
            </td>
            <td><b>{s.reference}</b></td>
            <td>{s.patient || '—'}</td>
            <td>{s.payer || '—'}</td>
            <td>{s.status || '—'}</td>
            <td>{formatCurrency(s.amount)}</td>
            <td>
              <button
                className={selectedKey === s.key ? 'wf-btn wf-btn--primary' : 'wf-btn wf-btn--ghost'}
                disabled={busy}
                onClick={() => onSelect(s)}
                style={{ padding: '5px 10px', fontSize: 12 }}
              >
                {selectedKey === s.key ? '✓ Selected' : 'Select'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DeadlinesCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetchAppealCalendar({ horizonDays: 60 });
      if (!r?.success) throw new Error(r?.message || 'Calendar failed');
      setRows(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const markFiled = async (id) => {
    try { await markDeadlineFiled(id); load(); } catch (e) { setError(e.message); }
  };

  return (
    <section className="wf-step-card">
      <header className="wf-step-card__head">
        <h2 className="wf-step-card__title" style={{ fontSize: 14 }}>
          <span className="wf-step-card__num" style={{ background: '#475569' }}>·</span>
          Filing deadlines (next 60 days)
        </h2>
      </header>
      {error && <div className="wf-error">{error}</div>}
      {busy ? (
        <p className="wf-step-card__hint">Loading calendar…</p>
      ) : rows.length === 0 ? (
        <p className="wf-step-card__hint">No tracked deadlines. They are auto-registered when a claim is denied.</p>
      ) : (
        <table className="suite-table">
          <thead>
            <tr>
              <th>Payer</th><th>Level</th><th>Received</th><th>Due</th><th>Days left</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 12).map((r) => (
              <tr key={r.DeadlineId}>
                <td>{r.PayerCode || r.PayerId || '—'}</td>
                <td>{r.AppealLevel}</td>
                <td>{r.ReceivedDate}</td>
                <td><b>{r.DueDate}</b></td>
                <td>{r.DaysRemaining}</td>
                <td>
                  {r.Status !== 'filed' && (
                    <button className="wf-btn wf-btn--ghost" onClick={() => markFiled(r.DeadlineId)} style={{ padding: '4px 9px', fontSize: 12 }}>
                      Mark filed
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function AppealBuilderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('appeal-builder');

  const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [packet, setPacket] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [reason, setReason] = useState('Request for reconsideration of denied claim');
  const [extra, setExtra] = useState('');
  const [files, setFiles] = useState([]);
  const [sample, setSample] = useState(null);
  const printRef = useRef(null);
  const detailsRef = useRef(null);

  const sampleId = searchParams.get('sample');

  useEffect(() => {
    (async () => {
      setBusy(true); setError('');
      try {
        const [denialsResp, claimsResp] = await Promise.all([
          listDenials({ pageSize: 25 }),
          listClaims({ pageSize: 25 }),
        ]);
        const denials = (denialsResp?.data?.items || []).map((d) => ({
          kind: 'denial', key: `d-${d.CaseId}`, caseId: d.CaseId,
          reference: d.ExternalId || d.ClaimNumber || (d.CaseId ? d.CaseId.slice(0, 8) : ''),
          patient: `${d.PatientFirstName || ''} ${d.PatientLastName || ''}`.trim(),
          payer: d.PayerName || d.PayerId, status: d.Status,
          amount: d.TotalDeniedAmount || d.DeniedAmount,
        }));
        const claims = (claimsResp?.data?.items || [])
          .filter((c) => ['denied', 'upheld', 'paid', 'finalized', 'appeal_in_review'].includes(String(c.Status).toLowerCase()))
          .map((c) => ({
            kind: 'claim', key: `c-${c.ClaimId}`, claimId: c.ClaimId,
            reference: c.ExternalId || c.PatientControlNumber || c.ClaimId?.slice(0, 8),
            patient: `${c.PatientFirstName || ''} ${c.PatientLastName || ''}`.trim(),
            payer: c.PayerName || c.PayerId, status: c.Status,
            amount: c.TotalChargeAmount,
          }));
        setSources([...denials, ...claims]);
      } catch (e) {
        setError(e?.response?.data?.message || e.message);
      } finally { setBusy(false); }
    })();
  }, []);

  const selectSource = (source) => {
    setSelected(source);
    setPacket(null);
    setPrediction(null);
    setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const build = async () => {
    if (!selected) return;
    setBusy(true); setError(''); setPacket(null); setPrediction(null);
    try {
      const body = { reason, narrative: extra };
      if (selected.kind === 'claim') body.claimId = selected.claimId;
      else if (selected.kind === 'denial') body.denialCaseId = selected.caseId;
      const data = await buildAppealPacket(body);
      if (!data?.success) throw new Error(data?.message || 'Build failed');
      setPacket(data.data);
      if (data.data?.prediction) {
        setPrediction(data.data.prediction);
      } else {
        try {
          const p = await fetchAppealPrediction(selected.kind === 'claim' ? { claimId: selected.claimId } : { denialCaseId: selected.caseId });
          if (p?.success) setPrediction(p.data);
        } catch { /* non-fatal */ }
      }
      try { await recomputeDeadlines(selected.kind === 'claim' ? { claimId: selected.claimId } : { denialCaseId: selected.caseId }); }
      catch { /* non-fatal */ }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const runSample = (s) => {
    setBusySampleId(s.id);
    setSample(s);
    const dp = s.payload.source || {};
    setSelected({
      kind: 'demo', key: `sample-${s.id}`,
      reference: s.title, patient: 'Demo Patient',
      payer: dp.payer || 'Demo Payer', amount: dp.deniedAmount,
    });
    setReason(s.payload.reason || reason);
    setExtra(s.payload.narrative || '');
    setPacket({
      source: 'demo',
      patient: { firstName: 'Demo', lastName: 'Patient', memberId: '—' },
      payer: { payerName: dp.payer || 'Demo Payer' },
      claimReference: { payerClaimControlNumber: 'DEMO' },
      denial: { carcCodes: [dp.carc].filter(Boolean), deniedAmount: dp.deniedAmount },
      submission: { deniedAmount: dp.deniedAmount },
      recommendedAction: 'file_appeal',
      evidenceChain: [
        { kind: 'cpt', cptCode: dp.cpt, basis: 'sample CPT' },
        { kind: 'icd', icd: dp.icd, basis: 'sample ICD' },
        { kind: 'lcd', policyId: 'L34579', title: 'Lumbar MRI LCD' },
      ],
      appealLetter:
`To ${dp.payer || 'Payer Appeals Department'},

This letter formally requests reconsideration of the denied service ${dp.cpt} (ICD ${dp.icd}) for our patient, which was denied under CARC ${dp.carc}.

${s.payload.narrative || ''}

The service meets the requirements outlined in LCD L34579 -- conservative therapy has been documented and the clinical findings on examination support the medical necessity of advanced imaging.

We respectfully request that this denial be overturned and the claim paid at the contracted rate. Supporting evidence is enclosed.

Sincerely,
Provider Office`,
    });
    setPrediction({ winProbability: 0.62, recommendation: 'File the appeal -- evidence is strong against the LCD.', topFactors: [
      { weight: 0.18, label: 'LCD criteria documented' },
      { weight: 0.12, label: 'Conservative therapy attempted' },
      { weight: -0.05, label: 'Payer denies CO-50 at higher-than-avg rate' },
    ] });
    setBusySampleId(null);
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'appeal-builder') runSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  const currentStep = packet ? 'results' : (selected ? 'run' : 'choose');
  const skippedSteps = ['upload'];
  const chatContext = useMemo(() => summariseSource(selected, packet, prediction), [selected, packet, prediction]);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      skippedSteps={skippedSteps}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">1</span>
            Pick a source claim or denial case
          </h2>
        </header>
        {error && <div className="wf-error">{error}</div>}
        <SourcePicker sources={sources || []} onSelect={selectSource} selectedKey={selected?.key} busy={busy} />
      </section>

      <section className="wf-step-card" ref={detailsRef}>
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">2</span>
            Narrative + supporting docs
          </h2>
        </header>
        {selected && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 13, color: '#1e40af' }}>
            Selected: <b>{selected.kind === 'claim' ? 'Claim' : 'Denial'} {selected.reference}</b>
            {selected.patient ? ` — ${selected.patient}` : ''}
            {selected.payer ? ` (${selected.payer})` : ''}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Reason for appeal
            <input value={reason} onChange={(e) => setReason(e.target.value)} style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', gridColumn: 'span 2' }}>
            Extra narrative
            <textarea value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Anything case-specific to add to the letter…" rows={3}
              style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontFamily: 'inherit', resize: 'vertical' }} />
          </label>
        </div>
        <p className="wf-step-card__hint" style={{ marginTop: 10 }}>{moduleDef.uploadHint}</p>
        <FileDropZone
          files={files}
          onFiles={setFiles}
          accept={moduleDef.accepts}
          multiple
          hint="Chart pages, prior EOBs, or policy excerpts -- attached to the case for the chat."
        />
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="wf-btn wf-btn--primary"
            disabled={!selected || busy}
            onClick={build}
            style={{ padding: '10px 24px', fontSize: 14 }}
          >
            {busy ? 'Building…' : 'Build packet'}
          </button>
        </div>
      </section>

      {prediction && (
        <section className="wf-step-card">
          <header className="wf-step-card__head">
            <h2 className="wf-step-card__title" style={{ fontSize: 14 }}>
              <span className="wf-step-card__num" style={{ background: '#16a34a' }}>↑</span>
              Win likelihood
            </h2>
          </header>
          <dl className="wf-kv">
            <div><dt>Estimated win probability</dt><dd>{Math.round((prediction.winProbability || 0) * 100)}%</dd></div>
            <div style={{ gridColumn: 'span 3' }}>
              <dt>Top factors</dt>
              <dd>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: 'var(--dm-ink)', fontSize: 13 }}>
                  {(prediction.topFactors || []).map((f, idx) => (
                    <li key={idx}><b>{f.weight > 0 ? '+' : ''}{Math.round(f.weight * 100)}%</b> {f.label}</li>
                  ))}
                </ul>
              </dd>
            </div>
          </dl>
          {prediction.recommendation && (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--dm-ink)' }}>{prediction.recommendation}</p>
          )}
        </section>
      )}

      {packet && (
        <ResultsPanel
          title="Appeal packet ready"
          data={packet}
          downloadName={`appeal-builder_${selected?.key || sample?.id || 'packet'}`}
          textReport={chatContext}
          printableRef={printRef}
        >
          <div ref={printRef}>
            <dl className="wf-kv">
              <div><dt>Patient</dt><dd>{(packet.patient?.firstName || packet.patient?.first_name || '') + ' ' + (packet.patient?.lastName || packet.patient?.last_name || '')}</dd></div>
              <div><dt>Payer</dt><dd>{packet.payer?.payerName || '—'}</dd></div>
              <div><dt>CARC codes</dt><dd>{(packet.denial?.carcCodes || []).join(', ') || '—'}</dd></div>
              <div><dt>Denied amount</dt><dd>{formatCurrency(packet.submission?.deniedAmount || packet.denial?.deniedAmount)}</dd></div>
              {packet.serviceDetail?.memberId && (
                <div><dt>Member ID</dt><dd>{packet.serviceDetail.memberId}</dd></div>
              )}
              {packet.serviceDetail?.dateOfService && (
                <div><dt>Date of service</dt><dd>{packet.serviceDetail.allDates?.join(', ') || packet.serviceDetail.dateOfService}</dd></div>
              )}
              {packet.serviceDetail?.providerName && (
                <div><dt>Provider</dt><dd>{packet.serviceDetail.providerName}{packet.serviceDetail.providerNpi ? ` (NPI: ${packet.serviceDetail.providerNpi})` : ''}</dd></div>
              )}
              {packet.claimReference?.payerClaimControlNumber && (
                <div><dt>Payer claim #</dt><dd>{packet.claimReference.payerClaimControlNumber}</dd></div>
              )}
              {packet.claimReference?.patientControlNumber && (
                <div><dt>Patient control #</dt><dd>{packet.claimReference.patientControlNumber}</dd></div>
              )}
              {packet.claimReference?.totalChargeAmount != null && (
                <div><dt>Total charge</dt><dd>{formatCurrency(packet.claimReference.totalChargeAmount)}</dd></div>
              )}
              <div><dt>Action</dt><dd>{(packet.recommendedAction || '').replace(/_/g, ' ') || '—'}</dd></div>
            </dl>

            {/* Policy Citations */}
            {(Object.keys(packet.policyCitations || {}).length > 0 || Object.keys(packet.payerPolicy || {}).length > 0) && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 8 }}>
                  Policy citations
                </div>
                {Object.entries(packet.policyCitations || {}).map(([artId, n]) => (
                  <div key={artId} style={{ marginBottom: 8, paddingLeft: 10, borderLeft: '3px solid #16d4a4', fontSize: 13, color: 'var(--dm-ink)' }}>
                    <b>Medicare Article {artId}</b>{n?.title ? ` — ${n.title}` : ''}
                    {n?.excerpt && <div style={{ color: 'var(--dm-muted)', fontSize: 12, marginTop: 2 }}>{n.excerpt}</div>}
                  </div>
                ))}
                {Object.entries((packet.payerPolicy || {}).narratives || {}).map(([pid, n]) => (
                  <div key={pid} style={{ marginBottom: 8, paddingLeft: 10, borderLeft: '3px solid #2261cc', fontSize: 13, color: 'var(--dm-ink)' }}>
                    <b>{packet.payer?.payerCode || 'Payer'} {pid}</b>{n?.title ? ` — ${n.title}` : ''}
                    {n?.description && <div style={{ color: 'var(--dm-muted)', fontSize: 12, marginTop: 2 }}>{n.description}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Documentation Gap */}
            {packet.documentationGap && (packet.documentationGap.present?.length > 0 || packet.documentationGap.missing?.length > 0) && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 8 }}>
                  Documentation gap
                </div>
                {(packet.documentationGap.present || []).length > 0 && (
                  <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13, color: '#16a085' }}>
                    {(packet.documentationGap.present || []).map((item, idx) => (
                      <li key={idx}>{typeof item === 'string' ? item : item.label || JSON.stringify(item)}</li>
                    ))}
                  </ul>
                )}
                {(packet.documentationGap.missing || []).length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#c0392b', marginBottom: 4 }}>Missing — request from provider:</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#c0392b' }}>
                      {(packet.documentationGap.missing || []).map((item, idx) => (
                        <li key={idx}>{item.label || item.kind || JSON.stringify(item)} {item.kind && item.label ? `(${item.kind})` : ''}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {(packet.evidenceChain || []).length > 0 && (
              <>
                <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)' }}>
                  Evidence chain ({(packet.evidenceChain || []).length})
                </div>
                <ul style={{ margin: '6px 0', paddingLeft: 18, color: 'var(--dm-ink)', fontSize: 13 }}>
                  {(packet.evidenceChain || []).map((e, idx) => (
                    <li key={idx}><b>{e.kind}</b> {e.cptCode || e.policyId || e.icd || e.column1 || ''} {e.title ? `— ${e.title}` : ''} {e.chargeAmount ? `($${e.chargeAmount})` : ''}</li>
                  ))}
                </ul>
              </>
            )}

            {/* Service-line detail table */}
            {(packet.evidenceChain || []).some(e => e.kind === 'service_line') && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 8 }}>
                  Service lines
                </div>
                <table className="suite-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr><th>CPT</th><th>Modifier</th><th>Units</th><th>Charge</th></tr>
                  </thead>
                  <tbody>
                    {(packet.evidenceChain || []).filter(e => e.kind === 'service_line').map((ln, idx) => (
                      <tr key={idx}>
                        <td><b>{ln.cptCode}</b></td>
                        <td>{(ln.modifiers || []).join(', ') || '—'}</td>
                        <td>{ln.units || 1}</td>
                        <td>{ln.chargeAmount != null ? `$${Number(ln.chargeAmount).toLocaleString()}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)' }}>
                Appeal letter
              </span>
              <button type="button" className="wf-btn wf-btn--ghost" style={{ padding: '4px 10px', fontSize: 11 }}
                onClick={() => { navigator.clipboard.writeText(packet.appealLetter || '').catch(() => {}); }}>
                Copy letter
              </button>
            </div>
            <pre className="wf-pre" style={{ marginTop: 6 }}>{packet.appealLetter}</pre>
          </div>
        </ResultsPanel>
      )}

      <DeadlinesCard />
    </WorkflowLayout>
  );
}
